import React, { useState, useEffect } from "react";
import axios from "axios";
import API_URL from "../../Config";
import { IoClose } from "react-icons/io5";
import "./purchaseIndents.css";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const AddPurchaseIndentPopup = ({ onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [indentId, setIndentId] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [indentData, setIndentData] = useState({
    customer_id: "",
    indent_date: "",
    iocl_approval_file: null,
    invoice_quantity_kl: "",
    invoice_no_of_barrels: "",
    invoice_file: null,
    acknowledgment_file: null,
  });

  const [errors, setErrors] = useState({});

  const tabs = [
    { id: "summary", label: "Summary", icon: "📋" },
    { id: "iocl", label: "IOCL Approval", icon: "✓" },
    { id: "invoice", label: "Invoice", icon: "🧾" },
    { id: "acknowledgment", label: "Acknowledgment", icon: "📄" },
  ];

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchCustomers = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      if (!authKey) {
        showSnackbar("Authorization key missing", "error");
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/barrel/customers/minimal`,
        {
          headers: { Authorization: authKey },
        }
      );

      const customerData = response.data?.customers ?? [];
      const formattedCustomers = customerData.map((cust) => ({
        id: cust.id,
        customer_name: cust.customer_name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showSnackbar("Error loading customers", "error");
    }
  };

  const fetchProducts = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      if (!authKey) {
        showSnackbar("Authorization key missing", "error");
        return;
      }

      const response = await axios.get(`${API_URL}/api/barrel/products`, {
        headers: { Authorization: authKey },
      });

      setProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      showSnackbar("Failed to load products", "error");
    }
  };

  // ------------------- INPUT HANDLERS -------------------

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    setIndentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setIndentData((prev) => ({ ...prev, [fieldName]: file }));
      if (errors[fieldName])
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleProductSelect = (e) => {
    const productId = e.target.value;
    if (!productId) return;

    // ✅ Check if product already selected
    const alreadySelected = selectedProducts.find((p) => p.id === productId);
    if (alreadySelected) {
      showSnackbar("Product already selected!", "error");
      e.target.value = ""; // reset dropdown
      return;
    }

    // ✅ Add new product
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProducts([
        ...selectedProducts,
        {
          id: product.id,
          product_code: product.product_code,
          name: product.product_name,
          quantity_kl: "",
          no_of_barrels: 0,
        },
      ]);
    }

    e.target.value = "";
  };

  const handleProductQuantityChange = (productId, value) => {
    setSelectedProducts(
      selectedProducts.map((product) => {
        if (product.id === productId) {
          const quantityKl = parseFloat(value) || 0;
          const liters = quantityKl * 1000;
          const barrels = Math.ceil(liters / 200);
          return { ...product, quantity_kl: value, no_of_barrels: barrels };
        }
        return product;
      })
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
  };

  // ------------------- SAVE HANDLER -------------------

  const handleSaveClick = async () => {
    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");

      // === 1️⃣ SAVE SUMMARY TAB ===
      if (activeTab === "summary") {
        if (!indentData.customer_id)
          return showSnackbar("Customer is required", "error");
        if (!indentData.indent_date)
          return showSnackbar("Indent date is required", "error");
        if (selectedProducts.length === 0)
          return showSnackbar("At least one product is required", "error");

        const items = selectedProducts.map((p) => ({
          product_code: p.product_code,
          product_name: p.name,
          quantity: p.quantity_kl,
          uom: "KL",
        }));

        const payload = {
          barrel_customer_id: indentData.customer_id,
          indent_date: indentData.indent_date,
          items,
        };

        const response = await axios.post(
          `${API_URL}/api/barrel/indent`,
          payload,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 201) {
          const newIndentId = response.data.indent_id;
          setIndentId(newIndentId);
          showSnackbar("Summary saved successfully!", "success");
          setActiveTab("iocl"); // move to next tab automatically
        }
      }

      // === 2️⃣ UPLOAD DOCUMENT TABS ===
      else if (["iocl", "invoice", "acknowledgment"].includes(activeTab)) {
        if (!indentId) {
          showSnackbar(
            "Please save the Summary tab first to create the indent.", "error"
          );
          return;
        }

        const formData = new FormData();
        const documentTypeMap = {
          iocl: "IOCL Approval",
          invoice: "Invoice",
          acknowledgment: "Acknowledgement",
        };

        formData.append("document_type", documentTypeMap[activeTab]);

        if (activeTab === "iocl" && indentData.iocl_approval_file) {
          formData.append("file", indentData.iocl_approval_file);
        }

        if (activeTab === "invoice" && indentData.invoice_file) {
          formData.append("file", indentData.invoice_file);
          formData.append(
            "invoice_quantity_kl",
            indentData.invoice_quantity_kl
          );
          formData.append("barrel_count", indentData.invoice_no_of_barrels);
        }

        if (activeTab === "acknowledgment" && indentData.acknowledgment_file) {
          formData.append("file", indentData.acknowledgment_file);
        }

        const uploadResponse = await axios.post(
          `${API_URL}/api/barrel/indent/${indentId}/upload`,
          formData,
          {
            headers: {
              Authorization: authKey,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (uploadResponse.status === 201) {
          showSnackbar(`${documentTypeMap[activeTab]} uploaded successfully!`, "success");

          // Automatically move to next tab
          const currentIndex = tabs.findIndex((t) => t.id === activeTab);
          if (currentIndex < tabs.length - 1) {
            setActiveTab(tabs[currentIndex + 1].id);
          } else {
            onSuccess(); // all done
          }
        }
      }
    } catch (error) {
      console.error("Error saving indent data:", error);
      showSnackbar(error.response?.data?.message || "Error saving indent data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------- RENDER TABS -------------------

  const renderSummaryTab = () => (
    <div className="purchase_indent_tab-content">
      <div className="purchase_indent_form-group">
        <label>
          Indent Date<span className="text-danger">*</span>
        </label>
        <input
          type="date"
          name="indent_date"
          value={indentData.indent_date}
          onChange={handleInputChange}
          className="purchase_indent_input"
        />
      </div>

      <div className="purchase_indent_form-group">
        <label>
          Customer<span className="text-danger">*</span>
        </label>
        <select
          name="customer_id"
          value={indentData.customer_id}
          onChange={handleInputChange}
          className="purchase_indent_input"
        >
          <option value="">Select customer</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.customer_name}
            </option>
          ))}
        </select>
      </div>

      <div className="purchase_indent_products-section">
        <div className="purchase_indent_products-header">
          <h5>
            Products<span className="text-danger">*</span>
          </h5>
          <select
            onChange={handleProductSelect}
            className="purchase_indent_product-dropdown"
          >
            <option value="">Select Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.product_name} ({p.product_code})
              </option>
            ))}
          </select>
        </div>

        <div className="purchase_indent_products-list">
          {selectedProducts.map((product) => (
            <div key={product.id} className="purchase_indent_product-card">
              <div className="purchase_indent_product-card-header">
                <h6>{product.name}</h6>
                <button
                  type="button"
                  onClick={() => handleRemoveProduct(product.id)}
                  className="purchase_indent_remove-btn"
                >
                  <IoClose />
                </button>
              </div>
              <div className="purchase_indent_product-card-body">
                <div className="purchase_indent_form-group">
                  <label>Quantity (KL)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={product.quantity_kl}
                    onChange={(e) =>
                      handleProductQuantityChange(product.id, e.target.value)
                    }
                    placeholder="Enter quantity in KL"
                    className="purchase_indent_input"
                  />
                </div>
                <div className="purchase_indent_form-group">
                  <label>No. of Barrels</label>
                  <input
                    type="text"
                    value={product.no_of_barrels}
                    readOnly
                    className="purchase_indent_input purchase_indent_readonly"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderIOCLTab = () => (
    <div className="purchase_indent_tab-content">
      <div className="purchase_indent_upload-section">
        <h5>IOCL Approval</h5>
        <div className="purchase_indent_form-group">
          <label>Upload Approval Copy</label>
          <div className="purchase_indent_file-upload">
            <input
              type="file"
              id="iocl_file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, "iocl_approval_file")}
              style={{ display: "none" }}
            />
            <label htmlFor="iocl_file" className="purchase_indent_upload-btn">
              <span>📁</span> Choose File
            </label>
            <span className="purchase_indent_file-name">
              {indentData.iocl_approval_file
                ? indentData.iocl_approval_file.name
                : "No file chosen"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvoiceTab = () => (
    <div className="purchase_indent_tab-content">
      <div className="purchase_indent_upload-section">
        <h5>Invoice Details</h5>

        <div className="purchase_indent_form-group">
          <label>As Per Invoice Product Quantity (in KL)</label>
          <input
            type="number"
            step="0.01"
            value={indentData.invoice_quantity_kl}
            onChange={(e) => {
              const q = e.target.value;
              const liters = parseFloat(q) * 1000 || 0;
              const barrels = Math.ceil(liters / 200);
              setIndentData((prev) => ({
                ...prev,
                invoice_quantity_kl: q,
                invoice_no_of_barrels: barrels,
              }));
            }}
            placeholder="Enter quantity in KL"
            className="purchase_indent_input"
          />
        </div>

        <div className="purchase_indent_form-group">
          <label>No. Of Barrel</label>
          <input
            type="text"
            value={indentData.invoice_no_of_barrels}
            readOnly
            placeholder="Auto-calculated"
            className="purchase_indent_input purchase_indent_readonly"
          />
        </div>

        <div className="purchase_indent_form-group">
          <label>Invoice Copy</label>
          <div className="purchase_indent_file-upload">
            <input
              type="file"
              id="invoice_file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, "invoice_file")}
              style={{ display: "none" }}
            />
            <label
              htmlFor="invoice_file"
              className="purchase_indent_upload-btn"
            >
              <span>📁</span> Choose File
            </label>
            <span className="purchase_indent_file-name">
              {indentData.invoice_file
                ? indentData.invoice_file.name
                : "No file chosen"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAcknowledgmentTab = () => (
    <div className="purchase_indent_tab-content">
      <div className="purchase_indent_upload-section">
        <h5>Invoice Acknowledgment</h5>

        <div className="purchase_indent_form-group">
          <label>Upload Acknowledgment Document</label>
          <div className="purchase_indent_file-upload">
            <input
              type="file"
              id="acknowledgment_file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, "acknowledgment_file")}
              style={{ display: "none" }}
            />
            <label
              htmlFor="acknowledgment_file"
              className="purchase_indent_upload-btn"
            >
              <span>📁</span> Choose File
            </label>
            <span className="purchase_indent_file-name">
              {indentData.acknowledgment_file
                ? indentData.acknowledgment_file.name
                : "No file chosen"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="purchase_indent_popup-overlay">
      <div className="purchase_indent_popup-modal">
        <div className="purchase_indent_popup-header">
          <h4>Add New Purchase Indent</h4>
          <span className="purchase_indent_close-btn" onClick={onClose}>
            <IoClose />
          </span>
        </div>

        {/* Tabs */}
        <div className="purchase_indent_tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`purchase_indent_tab ${
                activeTab === tab.id ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="purchase_indent_popup-body">
          {activeTab === "summary" && renderSummaryTab()}
          {activeTab === "iocl" && renderIOCLTab()}
          {activeTab === "invoice" && renderInvoiceTab()}
          {activeTab === "acknowledgment" && renderAcknowledgmentTab()}
        </div>

        <div className="purchase_indent_popup-footer">
          <button
            className="purchase_indent_btn purchase_indent_btn-save"
            onClick={handleSaveClick}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Current Tab"}
          </button>
          <button
            className="purchase_indent_btn purchase_indent_btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </div>
    </div>
  );
};

export default AddPurchaseIndentPopup;



















// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import API_URL from "../../Config";
// import { IoClose } from "react-icons/io5";
// import "./purchaseIndents.css";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";

// const AddPurchaseIndentPopup = ({ onClose, onSuccess }) => {
//   const [activeTab, setActiveTab] = useState("summary");
//   const [isLoading, setIsLoading] = useState(false);
//   const [customers, setCustomers] = useState([]);
//   const [selectedProducts, setSelectedProducts] = useState([]);
//   const [indentId, setIndentId] = useState(null);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [snackbarSeverity, setSnackbarSeverity] = useState("success");

//   const [indentData, setIndentData] = useState({
//     customer_id: "",
//     indent_date: "",
//     iocl_approval_file: null,
//     invoice_quantity_kl: "",
//     invoice_no_of_barrels: "",
//     invoice_file: null,
//     acknowledgment_file: null,
//   });

//   const [errors, setErrors] = useState({});

//   const productOptions = [
//     { id: "50763", name: "50763" },
//     { id: "52563", name: "52563" },
//   ];

//   const tabs = [
//     { id: "summary", label: "Summary", icon: "📋" },
//     { id: "iocl", label: "IOCL Approval", icon: "✓" },
//     { id: "invoice", label: "Invoice", icon: "🧾" },
//     { id: "acknowledgment", label: "Acknowledgment", icon: "📄" },
//   ];

//   const showSnackbar = (message, severity = "success") => {
//   setSnackbarMessage(message);
//   setSnackbarSeverity(severity);
//   setSnackbarOpen(true);
// };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return;
//     setSnackbarOpen(false);
//   };

//   useEffect(() => {
//     fetchCustomers();
//   }, []);

//   const fetchCustomers = async () => {
//     try {
//       const authKey = localStorage.getItem("authKey");
//       if (!authKey) {
//         showSnackbar("Authorization key missing", "error");
//         return;
//       }

//       const response = await axios.get(
//         `${API_URL}/api/barrel/customers/minimal`,
//         {
//           headers: { Authorization: authKey },
//         }
//       );

//       const customerData = response.data?.customers ?? [];
//       const formattedCustomers = customerData.map((cust) => ({
//         id: cust.id,
//         customer_name: cust.customer_name,
//       }));
//       setCustomers(formattedCustomers);
//     } catch (error) {
//       console.error("Error fetching customers:", error);
//       showSnackbar("Error loading customers", "error");
//     }
//   };

//   // ------------------- INPUT HANDLERS -------------------

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
//     setIndentData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleFileChange = (e, fieldName) => {
//     const file = e.target.files[0];
//     if (file) {
//       setIndentData((prev) => ({ ...prev, [fieldName]: file }));
//       if (errors[fieldName])
//         setErrors((prev) => ({ ...prev, [fieldName]: "" }));
//     }
//   };

//   // const handleProductSelect = (e) => {
//   //   const productId = e.target.value;
//   //   if (productId && !selectedProducts.find((p) => p.id === productId)) {
//   //     const product = productOptions.find((p) => p.id === productId);
//   //     setSelectedProducts([
//   //       ...selectedProducts,
//   //       {
//   //         id: product.id,
//   //         name: product.name,
//   //         quantity_kl: "",
//   //         no_of_barrels: 0,
//   //       },
//   //     ]);
//   //     e.target.value = "";
//   //   }
//   // };

//   const handleProductSelect = (e) => {
//   const productId = e.target.value;
//   if (!productId) return;

//   // ✅ Check if product already selected
//   const alreadySelected = selectedProducts.find((p) => p.id === productId);
//   if (alreadySelected) {
//     showSnackbar("Product already selected!", "error");
//     e.target.value = ""; // reset dropdown
//     return;
//   }

//   // ✅ Add new product
//   const product = productOptions.find((p) => p.id === productId);
//   if (product) {
//     setSelectedProducts([
//       ...selectedProducts,
//       {
//         id: product.id,
//         name: product.name,
//         quantity_kl: "",
//         no_of_barrels: 0,
//       },
//     ]);
//   }

//   e.target.value = "";
// };

//   const handleProductQuantityChange = (productId, value) => {
//     setSelectedProducts(
//       selectedProducts.map((product) => {
//         if (product.id === productId) {
//           const quantityKl = parseFloat(value) || 0;
//           const liters = quantityKl * 1000;
//           const barrels = Math.ceil(liters / 200);
//           return { ...product, quantity_kl: value, no_of_barrels: barrels };
//         }
//         return product;
//       })
//     );
//   };

//   const handleRemoveProduct = (productId) => {
//     setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
//   };

//   // ------------------- SAVE HANDLER -------------------

//   const handleSaveClick = async () => {
//     try {
//       setIsLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       // === 1️⃣ SAVE SUMMARY TAB ===
//       if (activeTab === "summary") {
//         if (!indentData.customer_id)
//           return showSnackbar("Customer is required", "error");
//         if (!indentData.indent_date)
//           return showSnackbar("Indent date is required", "error");
//         if (selectedProducts.length === 0)
//           return showSnackbar("At least one product is required", "error");

//         const items = selectedProducts.map((p) => ({
//           product_code: p.id,
//           product_name: p.name,
//           quantity: p.quantity_kl,
//           uom: "KL",
//         }));

//         const payload = {
//           barrel_customer_id: indentData.customer_id,
//           indent_date: indentData.indent_date,
//           items,
//         };

//         const response = await axios.post(
//           `${API_URL}/api/barrel/indent`,
//           payload,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         if (response.status === 201) {
//           const newIndentId = response.data.indent_id;
//           setIndentId(newIndentId);
//           showSnackbar("Summary saved successfully!", "success");
//           setActiveTab("iocl"); // move to next tab automatically
//         }
//       }

//       // === 2️⃣ UPLOAD DOCUMENT TABS ===
//       else if (["iocl", "invoice", "acknowledgment"].includes(activeTab)) {
//         if (!indentId) {
//           showSnackbar(
//             "Please save the Summary tab first to create the indent.", "error"
//           );
//           return;
//         }

//         const formData = new FormData();
//         const documentTypeMap = {
//           iocl: "IOCL Approval",
//           invoice: "Invoice",
//           acknowledgment: "Acknowledgement",
//         };

//         formData.append("document_type", documentTypeMap[activeTab]);

//         if (activeTab === "iocl" && indentData.iocl_approval_file) {
//           formData.append("file", indentData.iocl_approval_file);
//         }

//         if (activeTab === "invoice" && indentData.invoice_file) {
//           formData.append("file", indentData.invoice_file);
//           formData.append(
//             "invoice_quantity_kl",
//             indentData.invoice_quantity_kl
//           );
//           formData.append("barrel_count", indentData.invoice_no_of_barrels);
//         }

//         if (activeTab === "acknowledgment" && indentData.acknowledgment_file) {
//           formData.append("file", indentData.acknowledgment_file);
//         }

//         const uploadResponse = await axios.post(
//           `${API_URL}/api/barrel/indent/${indentId}/upload`,
//           formData,
//           {
//             headers: {
//               Authorization: authKey,
//               "Content-Type": "multipart/form-data",
//             },
//           }
//         );

//         if (uploadResponse.status === 201) {
//           showSnackbar(`${documentTypeMap[activeTab]} uploaded successfully!`, "success");

//           // Automatically move to next tab
//           const currentIndex = tabs.findIndex((t) => t.id === activeTab);
//           if (currentIndex < tabs.length - 1) {
//             setActiveTab(tabs[currentIndex + 1].id);
//           } else {
//             onSuccess(); // all done
//           }
//         }
//       }
//     } catch (error) {
//       console.error("Error saving indent data:", error);
//       showSnackbar(error.response?.data?.message || "Error saving indent data", "error");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // ------------------- RENDER TABS -------------------

//   const renderSummaryTab = () => (
//     <div className="purchase_indent_tab-content">
//       <div className="purchase_indent_form-group">
//         <label>
//           Indent Date<span className="text-danger">*</span>
//         </label>
//         <input
//           type="date"
//           name="indent_date"
//           value={indentData.indent_date}
//           onChange={handleInputChange}
//           className="purchase_indent_input"
//         />
//       </div>

//       <div className="purchase_indent_form-group">
//         <label>
//           Customer<span className="text-danger">*</span>
//         </label>
//         <select
//           name="customer_id"
//           value={indentData.customer_id}
//           onChange={handleInputChange}
//           className="purchase_indent_input"
//         >
//           <option value="">Select customer</option>
//           {customers.map((customer) => (
//             <option key={customer.id} value={customer.id}>
//               {customer.customer_name}
//             </option>
//           ))}
//         </select>
//       </div>

//       <div className="purchase_indent_products-section">
//         <div className="purchase_indent_products-header">
//           <h5>
//             Products<span className="text-danger">*</span>
//           </h5>
//           <select
//             onChange={handleProductSelect}
//             className="purchase_indent_product-dropdown"
//           >
//             <option value="">Select Product</option>
//             {productOptions.map((p) => (
//               <option key={p.id} value={p.id}>
//                 {p.name}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="purchase_indent_products-list">
//           {selectedProducts.map((product) => (
//             <div key={product.id} className="purchase_indent_product-card">
//               <div className="purchase_indent_product-card-header">
//                 <h6>{product.name}</h6>
//                 <button
//                   type="button"
//                   onClick={() => handleRemoveProduct(product.id)}
//                   className="purchase_indent_remove-btn"
//                 >
//                   <IoClose />
//                 </button>
//               </div>
//               <div className="purchase_indent_product-card-body">
//                 <div className="purchase_indent_form-group">
//                   <label>Quantity (KL)</label>
//                   <input
//                     type="number"
//                     step="0.01"
//                     value={product.quantity_kl}
//                     onChange={(e) =>
//                       handleProductQuantityChange(product.id, e.target.value)
//                     }
//                     placeholder="Enter quantity in KL"
//                     className="purchase_indent_input"
//                   />
//                 </div>
//                 <div className="purchase_indent_form-group">
//                   <label>No. of Barrels</label>
//                   <input
//                     type="text"
//                     value={product.no_of_barrels}
//                     readOnly
//                     className="purchase_indent_input purchase_indent_readonly"
//                   />
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );

//   const renderIOCLTab = () => (
//     <div className="purchase_indent_tab-content">
//       <div className="purchase_indent_upload-section">
//         <h5>IOCL Approval</h5>
//         <div className="purchase_indent_form-group">
//           <label>Upload Approval Copy</label>
//           <div className="purchase_indent_file-upload">
//             <input
//               type="file"
//               id="iocl_file"
//               accept=".pdf,.jpg,.jpeg,.png"
//               onChange={(e) => handleFileChange(e, "iocl_approval_file")}
//               style={{ display: "none" }}
//             />
//             <label htmlFor="iocl_file" className="purchase_indent_upload-btn">
//               <span>📁</span> Choose File
//             </label>
//             <span className="purchase_indent_file-name">
//               {indentData.iocl_approval_file
//                 ? indentData.iocl_approval_file.name
//                 : "No file chosen"}
//             </span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const renderInvoiceTab = () => (
//     <div className="purchase_indent_tab-content">
//       <div className="purchase_indent_upload-section">
//         <h5>Invoice Details</h5>

//         <div className="purchase_indent_form-group">
//           <label>As Per Invoice Product Quantity (in KL)</label>
//           <input
//             type="number"
//             step="0.01"
//             value={indentData.invoice_quantity_kl}
//             onChange={(e) => {
//               const q = e.target.value;
//               const liters = parseFloat(q) * 1000 || 0;
//               const barrels = Math.ceil(liters / 200);
//               setIndentData((prev) => ({
//                 ...prev,
//                 invoice_quantity_kl: q,
//                 invoice_no_of_barrels: barrels,
//               }));
//             }}
//             placeholder="Enter quantity in KL"
//             className="purchase_indent_input"
//           />
//         </div>

//         <div className="purchase_indent_form-group">
//           <label>No. Of Barrel</label>
//           <input
//             type="text"
//             value={indentData.invoice_no_of_barrels}
//             readOnly
//             placeholder="Auto-calculated"
//             className="purchase_indent_input purchase_indent_readonly"
//           />
//         </div>

//         <div className="purchase_indent_form-group">
//           <label>Invoice Copy</label>
//           <div className="purchase_indent_file-upload">
//             <input
//               type="file"
//               id="invoice_file"
//               accept=".pdf,.jpg,.jpeg,.png"
//               onChange={(e) => handleFileChange(e, "invoice_file")}
//               style={{ display: "none" }}
//             />
//             <label
//               htmlFor="invoice_file"
//               className="purchase_indent_upload-btn"
//             >
//               <span>📁</span> Choose File
//             </label>
//             <span className="purchase_indent_file-name">
//               {indentData.invoice_file
//                 ? indentData.invoice_file.name
//                 : "No file chosen"}
//             </span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const renderAcknowledgmentTab = () => (
//     <div className="purchase_indent_tab-content">
//       <div className="purchase_indent_upload-section">
//         <h5>Invoice Acknowledgment</h5>

//         <div className="purchase_indent_form-group">
//           <label>Upload Acknowledgment Document</label>
//           <div className="purchase_indent_file-upload">
//             <input
//               type="file"
//               id="acknowledgment_file"
//               accept=".pdf,.jpg,.jpeg,.png"
//               onChange={(e) => handleFileChange(e, "acknowledgment_file")}
//               style={{ display: "none" }}
//             />
//             <label
//               htmlFor="acknowledgment_file"
//               className="purchase_indent_upload-btn"
//             >
//               <span>📁</span> Choose File
//             </label>
//             <span className="purchase_indent_file-name">
//               {indentData.acknowledgment_file
//                 ? indentData.acknowledgment_file.name
//                 : "No file chosen"}
//             </span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="purchase_indent_popup-overlay">
//       <div className="purchase_indent_popup-modal">
//         <div className="purchase_indent_popup-header">
//           <h4>Add New Purchase Indent</h4>
//           <span className="purchase_indent_close-btn" onClick={onClose}>
//             <IoClose />
//           </span>
//         </div>

//         {/* Tabs */}
//         <div className="purchase_indent_tabs">
//           {tabs.map((tab) => (
//             <button
//               key={tab.id}
//               className={`purchase_indent_tab ${
//                 activeTab === tab.id ? "active" : ""
//               }`}
//               onClick={() => setActiveTab(tab.id)}
//             >
//               {tab.icon} {tab.label}
//             </button>
//           ))}
//         </div>

//         <div className="purchase_indent_popup-body">
//           {activeTab === "summary" && renderSummaryTab()}
//           {activeTab === "iocl" && renderIOCLTab()}
//           {activeTab === "invoice" && renderInvoiceTab()}
//           {activeTab === "acknowledgment" && renderAcknowledgmentTab()}
//         </div>

//         <div className="purchase_indent_popup-footer">
//           <button
//             className="purchase_indent_btn purchase_indent_btn-save"
//             onClick={handleSaveClick}
//             disabled={isLoading}
//           >
//             {isLoading ? "Saving..." : "Save Current Tab"}
//           </button>
//           <button
//             className="purchase_indent_btn purchase_indent_btn-cancel"
//             onClick={onClose}
//             disabled={isLoading}
//           >
//             Cancel
//           </button>
//         </div>

//         {/* Snackbar */}
//         <Snackbar
//           open={snackbarOpen}
//           autoHideDuration={6000}
//           onClose={handleSnackbarClose}
//           anchorOrigin={{ vertical: "top", horizontal: "right" }}
//           style={{ marginTop: "35px", marginLeft: "20px" }}
//         >
//           <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
//             {snackbarMessage}
//           </MuiAlert>
//         </Snackbar>
//       </div>
//     </div>
//   );
// };

// export default AddPurchaseIndentPopup;